import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Archive, Box, Mail, RotateCcw, Zap } from "feather-icons-react/build/IconComponents";
import Select from "react-select";
import { Filter } from "react-feather";
import Table from "../../core/pagination/datatable";
import useStockData from "../../hooks/useStockData";
import EditProduct from "../../core/modals/inventory/EditProduct";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { deleteProduct } from "../../api/inventoryApi";
import { removeProduct } from "../../slices/productSlice";
import { useDispatch } from "react-redux";

const ExpiredProduct = () => {
  // États pour gérer l'affichage des filtres et le modal d'édition
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedEditProduct, setSelectedEditProduct] = useState(null);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  const { products, categoryData, expiredList } = useStockData();
  const dispatch = useDispatch();

  // Préparation des options pour les filtres
  const productlist = products.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const category = categoryData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const store = [
    { value: "Bar", label: "Bar" },
    { value: "Exposition", label: "Exposition" },
    { value: "Cuisine", label: "Cuisine" },
    { value: "Stock général", label: "Stock général" },
  ];

  // Tooltips pour les boutons PDF, Excel et Refresh
  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  // Fonctions d'édition et de suppression
  const handleEdit = (product) => {
    setSelectedEditProduct(product);
    setIsEditModalVisible(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr ?",
      text: "Cette action est irréversible !",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Oui, supprimer !",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(removeProduct(id));
        deleteFunction(id);
      }
    });
  };

  const deleteFunction = async (productId) => {
    try {
      const response = await deleteProduct(productId);
      if (response?.error) {
        Swal.fire("Erreur", response.error.message || "Une erreur est survenue.", "error");
      } else {
        Swal.fire("Supprimé !", "Votre produit a été supprimé avec succès.", "success");
      }
    } catch (error) {
      Swal.fire(
        "Erreur",
        error?.response?.data?.message || "Une erreur imprévue est survenue.",
        "error"
      );
    }
  };

  // Définition des colonnes du tableau, avec la colonne "Action" incluant les boutons d'édition et de suppression
  const columns = [
    {
      title: "Créé par",
      dataIndex: "user",
      render: (user) => user?.name || "Non défini",
    },
    {
      title: "Stock",
      dataIndex: "stock",
      key: "stock",
    },
    {
      title: "Produit",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Catégorie",
      dataIndex: "category",
      render: (category) => category?.name || "Non défini",
    },
    {
      title: "Variantes",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.type}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Quantité",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.quantite}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Prix de vente",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.prix_vente}</strong> XOF
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Prix d'achat",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.prix_achat}</strong> XOF
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Qte alerte",
      dataIndex: "variations",
      key: "variations",
      render: (variations) =>
        variations && variations.length > 0 ? (
          <ul>
            {variations.map((variation, index) => (
              <li key={index}>
                <strong>{variation.alerte_quantite}</strong>
              </li>
            ))}
          </ul>
        ) : (
          "Aucune variante"
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <button
              className="btn btn-sm btn-primary me-2"
              onClick={() => handleEdit(record)}
            >
              <i data-feather="edit" className="feather-edit"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(record.id)}
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </button>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.createdby?.length || 0) - (b.createdby?.length || 0),
    },
  ];

  // Fonction pour générer le PDF
  const handleDownloadPDF = (data, title) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    doc.setFontSize(18);
    doc.text("Fiche d'expression de besoin", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(11);
    doc.text("LibrairieCafe", margin, 30);
    doc.text("Godomey/Abomey-Calavi", margin, 36);
    doc.text("Téléphone:................................", margin, 42);
    doc.text("Email: contact@complexeelite.com", margin, 48);
    const today = new Date();
    const formattedDate = today.toLocaleDateString("fr-FR");
    doc.text(`Date: ${formattedDate}`, pageWidth - margin, 30, { align: "right" });
    doc.text(`Bon n°: .........................`, pageWidth - margin, 36, { align: "right" });
    doc.line(margin, 52, pageWidth - margin, 52);
    const tableColumn = ["Produit", "Quantité", "Prix Achat (XOF)", "Prix Vente (XOF)"];
    const tableRows = [];
    data.forEach((item) => {
      const productName = item.name;
      const quantites = '';
      const prixAchat =
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_achat).join(" / ")
          : "N/A";
      const prixVente =
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_vente).join(" / ")
          : "N/A";
      tableRows.push([productName, quantites, prixAchat, prixVente]);
    });
    const startY = 60;
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: startY,
      margin: { left: margin, right: margin },
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
      didDrawPage: () => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.text(`Page ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: "right" });
      },
    });
    doc.save(`${title}.pdf`);
  };

  // Fonction pour générer le fichier Excel
  const handleDownloadExcel = (data, title) => {
    const exportData = data.map((item) => ({
      "Produit": item.name,
      "Catégorie": item.category?.name || "Non défini",
      "Quantité":
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.quantite).join(", ")
          : "N/A",
      "Prix Achat (XOF)":
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_achat).join(", ")
          : "N/A",
      "Prix Vente (XOF)":
        item.variations && item.variations.length > 0
          ? item.variations.map((v) => v.prix_vente).join(", ")
          : "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${title}.xlsx`);
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title me-auto">
              <h4>Produits expirés</h4>
              <h6>Gérez vos produits expirés</h6>
            </div>
            <ul className="table-top-head">
              <li>
                <div className="status-toggle d-flex justify-content-between align-items-center">
                  <input type="checkbox" id="user2" className="check" defaultChecked="true" />
                  <label htmlFor="user2" className="checktoggle">
                    checkbox
                  </label>
                  Notifier
                </div>
              </li>
              <li>
                <Link to="" className="btn btn-secondary" data-bs-toggle="modal" data-bs-target="#send-email">
                  <Mail className="feather-mail" />
                  Envoyer un e-mail
                </Link>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip}>
                  <Link onClick={() => handleDownloadPDF(expiredList, "Produits_expirés")}>
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                  <Link onClick={() => handleDownloadExcel(expiredList, "Produits_expirés")}>
                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link onClick={() => window.location.reload()}>
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>
          <div className="table-tab">
            <div className="tab-content" id="pills-tabContent">
              <div className="tab-pane fade show active" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab">
                <div className="card table-list-card">
                  <div className="card-body">
                    <div className="table-top">
                      <div className="search-set">
                        <div className="search-input">
                          <input type="text" placeholder="Search" className="form-control form-control-sm formsearch" />
                          <Link to="#" className="btn btn-searchset">
                            <i data-feather="search" className="feather-search" />
                          </Link>
                        </div>
                      </div>
                      <div className="search-path">
                        <Link className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} id="filter_search">
                          <Filter className="filter-icon" onClick={toggleFilterVisibility} />
                          <span onClick={toggleFilterVisibility}>
                            <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                          </span>
                        </Link>
                      </div>
                    </div>
                    <div className={`card${isFilterVisible ? " visible" : ""}`} id="filter_inputs" style={{ display: isFilterVisible ? "block" : "none" }}>
                      <div className="card-body pb-0">
                        <div className="row">
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Box className="info-img" />
                              <Select className="img-select" options={productlist} classNamePrefix="react-select" placeholder="Choisis Produit" />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Zap className="info-img" />
                              <Select className="img-select" options={category} classNamePrefix="react-select" placeholder="Choisis Catégorie" />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="input-blocks">
                              <Archive className="info-img" />
                              <Select className="img-select" options={store} classNamePrefix="react-select" placeholder="Choose Warehouse" />
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                            <div className="input-blocks">
                              <Link className="btn btn-filters ms-auto">
                                <i data-feather="search" className="feather-search" /> Search
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <Table columns={columns} dataSource={expiredList} />
                    </div>
                  </div>
                </div>
              </div>
              {isEditModalVisible && (
                <EditProduct 
                  product={selectedEditProduct} 
                  onClose={() => setIsEditModalVisible(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpiredProduct;
